import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';
import dayjs from 'dayjs';
import Snowfall from '../components/Snowfall';
import type {Event} from '../entities/Event';
import {remainingDays} from '../util/eventUtils';

interface Props {
  event: Event;
}

/** Days until the event ends, as a small tile in the navigation drawer. */
export default function AdventCountdown({event}: Props) {
  const days = remainingDays(event);
  if (days < 0) {
    return null;
  }
  const start = dayjs(event.start);
  const total = Math.max(dayjs(event.end).diff(start, 'day') + 1, 1);
  const elapsed = Math.min(Math.max(dayjs().startOf('day').diff(start, 'day'), 0), total);
  return (
    <Box
      sx={theme => ({
        position: 'relative',
        overflow: 'hidden',
        margin: '8px 10px 16px',
        padding: '14px 16px',
        borderRadius: '16px',
        color: theme.palette.secondary.contrastText,
        backgroundColor: theme.palette.secondary.main,
        backgroundImage: `linear-gradient(180deg, ${alpha('#ffffff', 0.08)}, transparent)`,
        boxShadow: `0 10px 20px -14px ${alpha(theme.palette.secondary.dark, 0.9)}`,
      })}
    >
      <Snowfall count={14}/>
      <Box sx={{position: 'relative'}}>
        <Typography
          variant="caption"
          sx={{display: 'block', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85, lineHeight: 1.4}}
        >
          {days === 0 ? 'Heute' : 'Nur noch'}
        </Typography>
        <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.75, marginTop: 0.25}}>
          {days > 0 && (
            <Typography component="span" sx={{fontSize: '2rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em'}}>
              {days}
            </Typography>
          )}
          <Typography component="span" sx={{fontSize: '1rem', fontWeight: 600, lineHeight: 1.2}}>
            {days === 0 ? 'ist der letzte Tag' : days === 1 ? 'Tag' : 'Tage'}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(elapsed / total) * 100}
          sx={theme => ({
            marginTop: 1.5,
            height: 4,
            backgroundColor: alpha('#ffffff', 0.25),
            '& .MuiLinearProgress-bar': {backgroundColor: theme.palette.secondary.contrastText},
          })}
        />
      </Box>
    </Box>
  );
}
